import {
  LABOR_STORE_NAMES,
  type LaborChartMetric,
  type LaborChartResponse,
  type LaborPoint,
  type LaborSeries,
  type LaborTimescale,
} from '@/services/laborAnalytics'
import { niceAxisScale } from '@/components/gcp/gcpHelpers'

export const TOTAL_SERIES_KEY = 'Total'

export function isTotalSeriesKey(key: string): boolean {
  return key.trim().toLowerCase() === 'total'
}

export function withTotalSeriesLast(series: LaborSeries[]): LaborSeries[] {
  const rest: LaborSeries[] = []
  const totals: LaborSeries[] = []
  for (const entry of series) {
    if (isTotalSeriesKey(entry.key)) {
      totals.push({ ...entry, key: TOTAL_SERIES_KEY })
    } else {
      rest.push(entry)
    }
  }
  return [...rest, ...totals]
}

export function withoutTotalSeries(series: LaborSeries[]): LaborSeries[] {
  return series.filter((entry) => !isTotalSeriesKey(entry.key))
}

export const LABOR_CHART_COLORS = [
  '#2563eb',
  '#dc2626',
  '#059669',
  '#d97706',
  '#7c3aed',
  '#db2777',
  '#0d9488',
  '#ea580c',
  '#4f46e5',
  '#65a30d',
  '#c026d3',
  '#0284c7',
  '#b45309',
  '#be123c',
  '#15803d',
  '#6d28d9',
  '#0e7490',
  '#a21caf',
  '#1d4ed8',
  '#ca8a04',
  '#9333ea',
  '#c2410c',
]

export function parseLaborValue(value: string | null | undefined): number {
  if (value == null || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Dates when the store had zero window sales and zero window hours (closed). */
export function buildStoreClosedDates(
  storeCharts: Record<LaborChartMetric, LaborChartResponse>,
): ReadonlySet<string> {
  const chart = storeCharts.gross_sales_per_hour
  if (!chart) return new Set()

  const totalSeries =
    chart.series.find((entry) => isTotalSeriesKey(entry.key)) ?? chart.series[0]
  if (!totalSeries) return new Set()

  const closed = new Set<string>()
  for (const point of totalSeries.points) {
    const sales = parseLaborValue(point.numerator)
    const hours = parseLaborValue(point.denominator)
    if (sales === 0 && hours === 0) {
      closed.add(point.date)
    }
  }
  return closed
}

export function seriesTotal(series: LaborSeries): number {
  return series.points.reduce((sum, point) => sum + parseLaborValue(point.value), 0)
}

export function getSeriesDates(seriesList: LaborSeries[]): string[] {
  const dates = new Set<string>()
  for (const series of seriesList) {
    for (const point of series.points) {
      dates.add(point.date)
    }
  }
  return [...dates].sort((a, b) => a.localeCompare(b))
}

export function getSeriesValueAt(series: LaborSeries, date: string): number {
  const point = series.points.find((entry) => entry.date === date)
  return parseLaborValue(point?.value)
}

/** AC-6c: keep top N series by total value; sum the rest into "Other". */
export function bucketTopNOther(series: LaborSeries[], n = 8): LaborSeries[] {
  if (series.length === 0) return []

  const sorted = [...series].sort((a, b) => seriesTotal(b) - seriesTotal(a))
  const top = sorted.slice(0, n)
  const rest = sorted.slice(n)
  if (rest.length === 0) return top

  const dates = getSeriesDates(series)
  const otherPoints = dates.map((date) => {
    const total = rest.reduce((sum, entry) => sum + getSeriesValueAt(entry, date), 0)
    return { date, value: String(total) }
  })

  return [...top, { key: 'Other', points: otherPoints }]
}

export function maxLaborSeriesValue(series: LaborSeries[]): number {
  let max = 0
  for (const entry of series) {
    for (const point of entry.points) {
      const value = parseLaborValue(point.value)
      if (Number.isFinite(value) && value > max) {
        max = value
      }
    }
  }
  return max
}

export function sharedLaborChartScale(
  seriesLists: LaborSeries[][],
): { max: number; ticks: number[] } {
  const dataMax = Math.max(0, ...seriesLists.map(maxLaborSeriesValue))
  return niceAxisScale(dataMax, 5)
}

export function unionSeriesColorKeys(seriesLists: LaborSeries[][]): string[] {
  const keys = new Set<string>()
  let hasTotal = false
  for (const series of seriesLists) {
    for (const entry of series) {
      if (isTotalSeriesKey(entry.key)) {
        hasTotal = true
      } else {
        keys.add(entry.key)
      }
    }
  }
  const sorted = [...keys].sort((a, b) => a.localeCompare(b))
  return hasTotal ? [...sorted, TOTAL_SERIES_KEY] : sorted
}

export function filterSeriesByHiddenKeys(
  series: LaborSeries[],
  hiddenKeys: ReadonlySet<string>,
): LaborSeries[] {
  if (hiddenKeys.size === 0) return series
  return series.filter((entry) => !hiddenKeys.has(entry.key))
}

export type SectionLoadState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: T }

export function sectionStateFromResult<T>(
  result: PromiseSettledResult<T>,
  isEmpty: (data: T) => boolean,
  fallbackMessage = 'Failed to load section',
): SectionLoadState<T> {
  if (result.status === 'rejected') {
    return {
      status: 'error',
      message: formatSectionError(result.reason, fallbackMessage),
    }
  }
  if (isEmpty(result.value)) {
    return { status: 'empty' }
  }
  return { status: 'ready', data: result.value }
}

export function formatSectionError(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  if (typeof error === 'string' && error.trim()) {
    return error
  }
  return fallback
}

export function colorForSeriesKey(key: string, index: number): string {
  if (isTotalSeriesKey(key)) {
    return '#111827'
  }
  if (key === 'Other') {
    return LABOR_CHART_COLORS[LABOR_CHART_COLORS.length - 1]!
  }
  return LABOR_CHART_COLORS[index % LABOR_CHART_COLORS.length]!
}

export function laborSeriesHasValues(series: LaborSeries[] | null | undefined): boolean {
  if (series == null) return false
  return series.some((entry) => entry.points.some((point) => parseLaborValue(point.value) !== 0))
}

export function formatLaborDateLabel(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) return date
  return `${Number(match[2])}/${Number(match[3])}`
}

/** Evenly spaced x-axis tick indexes, always including first and last. */
export function pickLaborXLabelIndexes(count: number, maxTicks = 6): number[] {
  if (count <= 0) return []
  if (count === 1) return [0]
  if (count <= maxTicks) return Array.from({ length: count }, (_, index) => index)

  const indexes: number[] = []
  for (let i = 0; i < maxTicks; i++) {
    indexes.push(Math.round((i * (count - 1)) / (maxTicks - 1)))
  }
  return [...new Set(indexes)]
}

/** Labor analytics ratios are decimals (0.25 = 25%). */
export function formatLaborTooltipPct(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function formatLaborAxisPct(value: number): string {
  return `${Math.round(value * 100).toLocaleString('en-US')}%`
}

export function formatHoursTooltip(value: number): string {
  return Math.round(value).toLocaleString('en-US')
}

export function formatHoursTooltipPct(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatHoursAxisPct(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')}%`
}

export function nextHiddenSeriesKeys(
  hidden: ReadonlySet<string>,
  key: string,
  allKeys: readonly string[],
): Set<string> {
  const next = new Set(hidden)
  if (next.has(key)) {
    next.delete(key)
    return next
  }
  const visible = allKeys.filter((item) => !next.has(item))
  if (visible.length <= 1) return next
  next.add(key)
  return next
}

export function convertHoursToPercentSeries(series: LaborSeries[]): LaborSeries[] {
  const dates = getSeriesDates(series)
  const parts = series.filter((entry) => !isTotalSeriesKey(entry.key))
  return series.map((entry) => ({
    key: isTotalSeriesKey(entry.key) ? TOTAL_SERIES_KEY : entry.key,
    points: dates.map((date) => {
      const value = getSeriesValueAt(entry, date)
      const total = parts.reduce((sum, other) => sum + getSeriesValueAt(other, date), 0)
      const pct = total === 0 ? null : (value / total) * 100
      return {
        date,
        value: pct == null ? null : String(pct),
        numerator: String(value),
        denominator: String(total),
      }
    }),
  }))
}

export function withRatioNumeratorDenominator(
  ratioSeries: LaborSeries[],
  numeratorSeries: LaborSeries[],
): LaborSeries[] {
  const ratioByKey = new Map(
    ratioSeries.map((entry) => [
      isTotalSeriesKey(entry.key) ? TOTAL_SERIES_KEY : entry.key,
      entry,
    ]),
  )
  const numeratorByKey = new Map(
    numeratorSeries.map((entry) => [
      isTotalSeriesKey(entry.key) ? TOTAL_SERIES_KEY : entry.key,
      entry,
    ]),
  )
  const keys = new Set([...ratioByKey.keys(), ...numeratorByKey.keys()])
  const orderedKeys = [
    ...ratioSeries
      .map((entry) => (isTotalSeriesKey(entry.key) ? TOTAL_SERIES_KEY : entry.key))
      .filter((key, index, list) => list.indexOf(key) === index),
    ...[...keys].filter((key) => !ratioByKey.has(key) && key !== TOTAL_SERIES_KEY),
  ]
  if (keys.has(TOTAL_SERIES_KEY) && !orderedKeys.includes(TOTAL_SERIES_KEY)) {
    orderedKeys.push(TOTAL_SERIES_KEY)
  }

  return orderedKeys.map((key) => {
    const ratioEntry = ratioByKey.get(key)
    const absolute = numeratorByKey.get(key)
    if (ratioEntry) {
      return {
        ...ratioEntry,
        key,
        points: ratioEntry.points.map((point) => {
          if (
            point.numerator != null &&
            point.numerator !== '' &&
            point.denominator != null &&
            point.denominator !== ''
          ) {
            return point
          }
          const numerator = absolute ? getSeriesValueAt(absolute, point.date) : 0
          const ratio = parseLaborValue(point.value)
          const denominator = ratio === 0 ? 0 : numerator / ratio
          return {
            ...point,
            numerator: String(numerator),
            denominator: String(denominator),
          }
        }),
      }
    }
    if (!absolute) {
      return { key, points: [] }
    }
    return {
      key,
      points: absolute.points.map((point) => ({
        date: point.date,
        value: null,
        numerator: point.value ?? '0',
        denominator: '0',
      })),
    }
  })
}

export interface LaborPctTooltipFormatters {
  numeratorLabel: string
  denominatorLabel: string
  ratioLabel?: string
  formatNumerator: (value: number) => string
  formatDenominator: (value: number) => string
  formatRatio: (value: number) => string
}

export interface LaborPctTooltipPoint {
  value: number | null
  numerator: number | null
  denominator: number | null
}

export interface LaborPctTooltipCells {
  numerator: string
  denominator: string
  ratio: string
}

export function formatLaborPctTooltipCells(
  point: LaborPctTooltipPoint,
  formatters: LaborPctTooltipFormatters,
): LaborPctTooltipCells {
  const { value, numerator, denominator } = point
  return {
    numerator: numerator == null ? '—' : formatters.formatNumerator(numerator),
    denominator: denominator == null ? '—' : formatters.formatDenominator(denominator),
    ratio:
      value == null
        ? '—'
        : denominator === 0
          ? '—'
          : formatters.formatRatio(value),
  }
}

function seriesHasRatioPoints(series: LaborSeries[]): boolean {
  return series.some((entry) =>
    entry.points.some(
      (point) =>
        point.numerator != null &&
        point.numerator !== '' &&
        point.denominator != null &&
        point.denominator !== '',
    ),
  )
}

function stationSalesAt(series: LaborSeries[], station: string, date: string): number {
  const point = series.find((entry) => entry.key === station)?.points.find((p) => p.date === date)
  if (point?.denominator != null && point.denominator !== '') {
    return parseLaborValue(point.denominator)
  }
  return 0
}

function hoursAt(
  regular: LaborSeries[],
  overtime: LaborSeries[],
  station: string,
  date: string,
): number {
  const reg = regular.find((entry) => entry.key === station)
  const ot = overtime.find((entry) => entry.key === station)
  return getSeriesValueAt(reg ?? { key: '', points: [] }, date) +
    getSeriesValueAt(ot ?? { key: '', points: [] }, date)
}

/** Filter station-breakdown series and recompute Total (absolute or ratio-of-sums). */
export function filterStationBreakdownSeries(
  series: LaborSeries[],
  stations: string[],
): LaborSeries[] {
  const selected = new Set(stations)
  const filtered = series.filter(
    (entry) => !isTotalSeriesKey(entry.key) && selected.has(entry.key),
  )
  const dates = getSeriesDates(filtered.length > 0 ? filtered : series)
  const ratio = seriesHasRatioPoints(filtered)

  const totalPoints = dates.map((date) => {
    if (filtered.length === 0) {
      return { date, value: '0', ...(ratio ? { numerator: '0', denominator: '0' } : {}) }
    }
    if (ratio) {
      let numerator = 0
      let denominator = 0
      for (const entry of filtered) {
        const point = entry.points.find((p) => p.date === date)
        if (point?.numerator != null && point.numerator !== '' && point.denominator != null && point.denominator !== '') {
          numerator += parseLaborValue(point.numerator)
          denominator += parseLaborValue(point.denominator)
        }
      }
      const value = denominator === 0 ? null : numerator / denominator
      return {
        date,
        value: value == null ? null : String(value),
        numerator: String(numerator),
        denominator: String(denominator),
      }
    }
    const sum = filtered.reduce((acc, entry) => acc + getSeriesValueAt(entry, date), 0)
    return { date, value: String(sum) }
  })

  return withTotalSeriesLast([...filtered, { key: TOTAL_SERIES_KEY, points: totalPoints }])
}

/** Resolve selected stations vs role-assigned allowlist for chart filtering. */
export function resolveStationChartFilter(
  selectedStations: string[],
  allowlist: string[] | null,
): string[] | null {
  if (allowlist === null) return null
  if (selectedStations.length > 0) return selectedStations
  return allowlist
}

function rebuildGrossSalesPerHour(
  charts: Record<LaborChartMetric, LaborChartResponse>,
  stations: string[],
): LaborChartResponse {
  const chart = charts.gross_sales_per_hour
  if (stations.length === 0) return chart

  const regular = charts.regular_hours_by_station.series
  const overtime = charts.overtime_hours_by_station.series
  const wagesPct = withRatioNumeratorDenominator(
    charts.wages_pct_of_station_sales.series,
    charts.wages_by_station.series,
  )
  const dates = getSeriesDates([...regular, ...overtime, ...wagesPct])

  const totalPoints = dates.map((date) => {
    let hours = 0
    let sales = 0
    for (const station of stations) {
      hours += hoursAt(regular, overtime, station, date)
      sales += stationSalesAt(wagesPct, station, date)
    }
    const value = hours === 0 ? null : sales / hours
    return {
      date,
      value: value == null ? null : String(value),
      numerator: String(sales),
      denominator: String(hours),
    }
  })

  return { ...chart, series: [{ key: TOTAL_SERIES_KEY, points: totalPoints }] }
}

function rebuildWagesPctOfSales(
  charts: Record<LaborChartMetric, LaborChartResponse>,
  stations: string[],
): LaborChartResponse {
  const chart = charts.wages_pct_of_sales
  if (stations.length === 0) return chart

  const wages = charts.wages_by_station.series
  const wagesPct = withRatioNumeratorDenominator(
    charts.wages_pct_of_station_sales.series,
    wages,
  )
  const dates = getSeriesDates(wages)

  const totalPoints = dates.map((date) => {
    let wageSum = 0
    let salesSum = 0
    for (const station of stations) {
      const wageSeries = wages.find((entry) => entry.key === station)
      wageSum += getSeriesValueAt(wageSeries ?? { key: '', points: [] }, date)
      salesSum += stationSalesAt(wagesPct, station, date)
    }
    const value = salesSum === 0 ? null : wageSum / salesSum
    return {
      date,
      value: value == null ? null : String(value),
      numerator: String(wageSum),
      denominator: String(salesSum),
    }
  })

  return { ...chart, series: [{ key: TOTAL_SERIES_KEY, points: totalPoints }] }
}

/** Apply station filter to one store's chart bundle (all-stations payload in). */
export function filterStoreChartsByStations(
  charts: Record<LaborChartMetric, LaborChartResponse>,
  stations: string[],
  rebuildStoreLevel = stations.length > 0,
): Record<LaborChartMetric, LaborChartResponse> {
  const wagesByStation = {
    ...charts.wages_by_station,
    series: filterStationBreakdownSeries(charts.wages_by_station.series, stations),
  }
  const wagesPctRaw = withRatioNumeratorDenominator(
    charts.wages_pct_of_station_sales.series,
    charts.wages_by_station.series,
  )

  const stationBreakdown = {
    regular_hours_by_station: {
      ...charts.regular_hours_by_station,
      series: filterStationBreakdownSeries(charts.regular_hours_by_station.series, stations),
    },
    overtime_hours_by_station: {
      ...charts.overtime_hours_by_station,
      series: filterStationBreakdownSeries(charts.overtime_hours_by_station.series, stations),
    },
    wages_by_station: wagesByStation,
    wages_pct_of_station_sales: {
      ...charts.wages_pct_of_station_sales,
      series: filterStationBreakdownSeries(wagesPctRaw, stations),
    },
    avg_hourly_wages: {
      ...charts.avg_hourly_wages,
      series: filterStationBreakdownSeries(charts.avg_hourly_wages.series, stations),
    },
  }

  if (!rebuildStoreLevel) {
    return {
      ...charts,
      ...stationBreakdown,
    }
  }

  return {
    ...charts,
    ...stationBreakdown,
    gross_sales_per_hour: rebuildGrossSalesPerHour(charts, stations),
    wages_pct_of_sales: rebuildWagesPctOfSales(charts, stations),
  }
}

export function filterChartsByStoreByStations(
  chartsByStore: Record<string, Record<LaborChartMetric, LaborChartResponse>> | undefined,
  selectedStations: string[],
  allowlist: string[] | null = null,
): Record<string, Record<LaborChartMetric, LaborChartResponse>> | undefined {
  if (!chartsByStore) return undefined
  const effective = resolveStationChartFilter(selectedStations, allowlist)
  if (effective === null) return chartsByStore

  const rebuildStoreLevel = selectedStations.length > 0
  const result: Record<string, Record<LaborChartMetric, LaborChartResponse>> = {}
  for (const store of LABOR_STORE_NAMES) {
    const charts = chartsByStore[store]
    if (charts) {
      result[store] = filterStoreChartsByStations(charts, effective, rebuildStoreLevel)
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Timescale aggregation (client-side from L1 daily grain)
// ---------------------------------------------------------------------------

function parseIsoDateUtc(iso: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) throw new Error(`Invalid ISO date: ${iso}`)
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

function formatIsoDateUtc(ms: number): string {
  const date = new Date(ms)
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addIsoDays(iso: string, days: number): string {
  return formatIsoDateUtc(parseIsoDateUtc(iso) + days * 86_400_000)
}

function iterIsoDatesInclusive(start: string, end: string): string[] {
  const dates: string[] = []
  let current = start
  while (current <= end) {
    dates.push(current)
    current = addIsoDays(current, 1)
  }
  return dates
}

export function windowStart(isoDate: string, timescale: LaborTimescale): string {
  if (timescale === 'L1') return isoDate
  if (timescale === 'L7') return addIsoDays(isoDate, -6)
  if (timescale === 'L30') return addIsoDays(isoDate, -29)
  if (timescale === 'YTD') return `${isoDate.slice(0, 4)}-01-01`
  if (timescale === 'LTM') return addIsoDays(isoDate, -364)
  throw new Error(`Unknown timescale: ${timescale}`)
}

export function windowIsComplete(
  isoDate: string,
  timescale: LaborTimescale,
  seriesStart: string,
): boolean {
  return windowStart(isoDate, timescale) >= seriesStart
}

function seriesPointHasRatio(point: LaborPoint): boolean {
  return (
    point.numerator != null &&
    point.numerator !== '' &&
    point.denominator != null &&
    point.denominator !== ''
  )
}

function laborSeriesHasRatioPoints(series: LaborSeries): boolean {
  return series.points.some(seriesPointHasRatio)
}

function firstSeriesPointDate(series: LaborSeries): string {
  if (series.points.length === 0) return ''
  return [...series.points].sort((a, b) => a.date.localeCompare(b.date))[0]!.date
}

export function aggregateLaborSeries(
  series: LaborSeries,
  timescale: LaborTimescale,
  displayStart: string,
  displayEnd: string,
  seriesStart: string,
): LaborSeries {
  if (timescale === 'L1') {
    return {
      key: series.key,
      points: [...series.points]
        .filter((point) => point.date >= displayStart && point.date <= displayEnd)
        .sort((a, b) => a.date.localeCompare(b.date)),
    }
  }

  const isRatio = laborSeriesHasRatioPoints(series)
  const daily = new Map<
    string,
    { value: number; numerator?: number; denominator?: number }
  >()
  for (const point of series.points) {
    daily.set(point.date, {
      value: parseLaborValue(point.value),
      numerator: seriesPointHasRatio(point) ? parseLaborValue(point.numerator) : undefined,
      denominator: seriesPointHasRatio(point) ? parseLaborValue(point.denominator) : undefined,
    })
  }

  const points: LaborPoint[] = []
  for (const date of iterIsoDatesInclusive(displayStart, displayEnd)) {
    if (!windowIsComplete(date, timescale, seriesStart)) continue
    const windowDates = iterIsoDatesInclusive(windowStart(date, timescale), date)
    if (isRatio) {
      let numerator = 0
      let denominator = 0
      for (const day of windowDates) {
        const entry = daily.get(day)
        numerator += entry?.numerator ?? 0
        denominator += entry?.denominator ?? 0
      }
      points.push({
        date,
        value: denominator === 0 ? null : String(numerator / denominator),
        numerator: String(numerator),
        denominator: String(denominator),
      })
    } else {
      let sum = 0
      for (const day of windowDates) {
        sum += daily.get(day)?.value ?? 0
      }
      points.push({ date, value: String(sum) })
    }
  }

  return { key: series.key, points }
}

export function aggregateChartsByStore(
  chartsByStore: Record<string, Record<LaborChartMetric, LaborChartResponse>>,
  timescale: LaborTimescale,
  displayStart: string,
  displayEnd: string,
): Record<string, Record<LaborChartMetric, LaborChartResponse>> {
  const result: Record<string, Record<LaborChartMetric, LaborChartResponse>> = {}
  for (const [store, charts] of Object.entries(chartsByStore)) {
    const aggregated = {} as Record<LaborChartMetric, LaborChartResponse>
    for (const metric of Object.keys(charts) as LaborChartMetric[]) {
      const chart = charts[metric]
      if (!chart) continue
      aggregated[metric] = {
        ...chart,
        timescale,
        start: displayStart,
        end: displayEnd,
        series: chart.series.map((series) => {
          const start = firstSeriesPointDate(series) || displayStart
          return aggregateLaborSeries(series, timescale, displayStart, displayEnd, start)
        }),
      }
    }
    result[store] = aggregated
  }
  return result
}
